import {
    Dispatch,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react'

import Axios from 'axios'
import Config from '../config'
import DayJs from 'dayjs'
import { ROLES } from '../constants'
import GA from '../router/GoogleAnalytics'
import firebase from 'firebase/compat/app'
import { jwtDecode } from 'jwt-decode'
import relativeTime from 'dayjs/plugin/relativeTime'
import { setNewAccessToken } from '../apis/apis'
import storageHelper from '../helpers/storage'
import useLocalStorage from '../hooks/useLocalStorage'
import useSessionStorage from '../hooks/useSessionStorage'
import { useToken } from '../hooks/useToken'
import i18n from '../i18n'
import { authBroadcast } from '../services/AuthBroadcast'
import { resolveRolePromotion } from '../helpers/rolePromotion'
import { signOutFirebaseSession } from '../helpers/firebaseSession'
import { authReady } from '../firebase'

DayJs.extend(relativeTime)

const SESSION_RECOVERY_TIMEOUT_MS = 8000

const notContextFn = () => {
    throw Error('Not inside Context')
}
const contextOutOfContext = {
    setRole: notContextFn as (role: ROLES) => void,
    setToken: notContextFn as (token: string) => void,
    fbUser: undefined as firebase.User | null | undefined,
    user: undefined as User | null | undefined,
    profiles: [] as Profile[],
    setUser: notContextFn as (user: Partial<User>) => Promise<IPatchUserResponse>,
    fetchUser: notContextFn as () => void,
    setUserButNotSaveIt: notContextFn as Dispatch<SetStateAction<User | null | undefined>>, //TODO: Deprecate this and handle everything inside this context
    logout: notContextFn as (next?: string) => Promise<void>,
    login: notContextFn as (email: string, password: string, force?: boolean) => Promise<void>,
    loginSuperAdmin: notContextFn as (token: string) => Promise<unknown>,
    loginWithIdToken: notContextFn as (idToken: string) => Promise<boolean>,
    verify: notContextFn as (code: string, next?: string) => Promise<unknown>,
    setProfiles: notContextFn as (profiles: Profile[]) => unknown,
    role: undefined as ROLES | undefined,
    logged: undefined as boolean | undefined,
    token: undefined as string | undefined,
    skipValidation: false as boolean,
    setSkipValidation: notContextFn as (value: boolean) => void,
    handleUploadPhoto: notContextFn as (fileToUpload: Blob, userId: string) => void,
    imageIsLoading: false as boolean,
    setImageIsLoading: notContextFn as (value: boolean) => void,
    isCoach: false as boolean,
    isRecoveringSession: true as boolean,
}

export type ContextInterface = typeof contextOutOfContext

export const UserContext = createContext<ContextInterface>(contextOutOfContext)
UserContext.displayName = 'UserContext'

export const useUserContextValue = () => {
    const { token: jwtToken } = useToken()
    const [user, setUser] = useState<ContextInterface['user']>()
    const [role, setRole] = useLocalStorage<ROLES | undefined>('role', undefined)
    const [profiles, setProfiles] = useState<Profile[]>([])
    const [fbUser, setFBUser] = useState<ContextInterface['fbUser']>()

    // MIGRADO: sessionStorage para aislamiento por pestaña
    const [token, setToken] = useSessionStorage<string | undefined>('token', undefined)
    const [skipValidation, setSkipValidation] = useState<boolean>(false)
    const [imageIsLoading, setImageIsLoading] = useState<boolean>(false)
    const [isRecoveringSession, setIsRecoveringSession] = useState(true)
    // Persistir en sessionStorage para que sobreviva reloads
    const [hasLoggedOutIntentionally, setHasLoggedOutIntentionally] = useSessionStorage<boolean>('hasLoggedOut', false)

    // Protección de Race Condition: evitar que otra pestaña sobrescriba nuestra sesión
    // Lógica: Si tenemos token válido, ignorar cambios de Firebase de otras pestañas
    const handleAuthStateChanged = useCallback(
        async (firebaseUser: firebase.User | null) => {
            // eslint-disable-next-line no-console
            console.info('[Auth] onAuthStateChanged:', {
                firebaseUser: firebaseUser?.uid?.slice(0, 8) || null,
                currentFbUser: fbUser?.uid?.slice(0, 8) || null,
                hasToken: !!token,
                isRecoveringSession,
            })

            // Firebase reporta logout (otra pestaña hizo signOut)
            if (firebaseUser === null) {
                // Si tenemos token válido, IGNORAR - nuestra sesión sigue activa
                if (token && fbUser) {
                    // eslint-disable-next-line no-console
                    console.warn('[Auth Guard] Ignorando signOut de Firebase (otra pestaña cerró sesión).')
                    return
                }
                setFBUser(null)
                setIsRecoveringSession(false)
                return
            }

            // Sin sesión previa: aceptar y recuperar token si no tenemos
            if (!fbUser) {
                setFBUser(firebaseUser)

                // Si no tenemos token, intentar recuperar (excepto si fue logout intencional)
                if (!token && isRecoveringSession && !hasLoggedOutIntentionally) {
                    try {
                        // eslint-disable-next-line no-console
                        console.info('[Auth] Recuperando token para nuevo fbUser...')
                        const idToken = await firebaseUser.getIdToken(true)
                        // Llamada directa a API (no usar verifyIdToken por orden de declaración)
                        const response = await Axios.post(Config.API + '/user/verifyToken', { idToken })
                        setToken(response.data.token)
                        // eslint-disable-next-line no-console
                        console.info('[Auth] Token recuperado exitosamente')
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.warn('[Auth] Error recuperando token:', error)
                    } finally {
                        setIsRecoveringSession(false)
                    }
                } else if (hasLoggedOutIntentionally) {
                    // eslint-disable-next-line no-console
                    console.info('[Auth] Logout intencional, no recuperando token')
                    setIsRecoveringSession(false)
                } else {
                    // No estábamos en modo de recuperación ni hubo logout intencional.
                    // Asegurar que el flag de recuperación no quede en true por accidente.
                    setIsRecoveringSession(false)
                }
                return
            }

            // Mismo usuario de Firebase: aceptar (es un refresh normal del token)
            if (fbUser.uid === firebaseUser.uid) {
                setFBUser(firebaseUser)
                return
            }

            // UID diferente: otra pestaña inició sesión con otro usuario
            // Si tenemos token válido, proteger esta sesión
            if (token) {
                // eslint-disable-next-line no-console
                console.warn(
                    '[Auth Guard] Ignorando cambio de usuario de Firebase (otra pestaña). ' +
                        `Actual: ${fbUser.uid.slice(0, 8)}... Nuevo: ${firebaseUser.uid.slice(0, 8)}...`
                )
                return
            }

            // Sin token: permitir el cambio (sesión no establecida aún)
            setFBUser(firebaseUser)
        },
        [fbUser, token, isRecoveringSession, setToken, setIsRecoveringSession, hasLoggedOutIntentionally]
    )

    const loginWithIdToken = async (idToken: string) => {
        try {
            setSkipValidation(true)
            setHasLoggedOutIntentionally(false)
            const { user } = await firebase.auth().signInWithCustomToken(idToken)
            if (!user) throw Error('User not found')
            setFBUser(user)
            const token = await user?.getIdToken()
            const { data } = await Axios.post<{ token: string }>(`${Config.API}/user/verifyToken`, {
                idToken: token,
            })
            setToken(data.token)
            return true
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error)
            throw error
        }
    }

    const loginSuperAdmin = async (token: string) => {
        try {
            setHasLoggedOutIntentionally(false)
            const { user: adminUser } = await firebase.auth().signInWithCustomToken(token)
            const idToken = await adminUser?.getIdToken()
            idToken && (await verifyIdToken(idToken))
        } catch (error) {
            return error
        }
    }

    const verifyIdToken = useCallback(
        async (idToken: string) => {
            const response = await Axios.post(Config.API + '/user/verifyToken', { idToken })
            const token = response.data.token
            setToken(token)
            return token
        },
        [setToken]
    )

    const handleUploadPhoto = async (fileToUpload: Blob, userId: string) => {
        try {
            setImageIsLoading(true)

            const formData = new FormData()
            formData.append('file', fileToUpload)
            const response = await fetch(`${process.env.REACT_APP_S3}/{${userId}`, {
                method: 'POST',
                body: formData,
                headers: {
                    Authorization: `${token}`,
                },
            })
            if (!response.ok) throw new Error('Error uploading image')

            const json = await response.json()
            const urlImage = json.thumbnailUrl.split('?AWSAccessKeyId')[0] + `?hash=${Date.now()}`
            await saveUser({ ...user, urlImage })

            //window.location.reload()
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error uploading image', error)
        } finally {
            setImageIsLoading(false)
        }
    }

    const logoutWithoutUser = useCallback(
        async (opts?: { broadcast?: boolean }) => {
            const oldUser = { ...user }
            const oldFBUser = fbUser
            const oldToken = token
            const currentToken = token

            if (typeof window !== 'undefined' && 'Intercom' in window) {
                // @ts-ignore
                window.Intercom('shutdown')
            }
            try {
                // Mark intentional logout BEFORE clearing token to avoid race where
                // other tabs see token removed but not the logout flag.
                try {
                    storageHelper.setHasLoggedOut(true)
                    setHasLoggedOutIntentionally(true)
                } catch (e) {
                    // ignore
                }

                setUser(undefined)
                setFBUser(null)
                setToken(undefined)

                // Notificar a otras pestañas CON EL MISMO TOKEN (a menos que se pida lo contrario)
                if (currentToken && opts?.broadcast !== false) {
                    try {
                        authBroadcast.broadcastLogout(currentToken)
                    } catch (e) {
                        // Restore previous auth state to avoid partial logout
                        setUser(oldUser as User)
                        setFBUser(oldFBUser)
                        setToken(oldToken)
                        try {
                            storageHelper.setHasLoggedOut(false)
                            setHasLoggedOutIntentionally(false)
                        } catch (er) {
                            // ignore
                        }
                        throw e
                    }
                }

                storageHelper.clearSession()

                // Cerrar la sesión de Firebase de esta pestaña. Sin esto el logout es solo local y al
                // recargar `recoverSessionFromFirebase` canjea el refresh token y revive la sesión.
                // La persistencia es por pestaña (`SESSION`), así que esto no alcanza a las demás.
                await signOutFirebaseSession()
            } catch (error) {
                setUser(oldUser as User)
                setFBUser(oldFBUser)
                setToken(oldToken)
                try {
                    storageHelper.setHasLoggedOut(false)
                    setHasLoggedOutIntentionally(false)
                } catch (er) {
                    // ignore
                }
                throw error
            }
        },
        [user, fbUser, token, setHasLoggedOutIntentionally, setUser, setFBUser, setToken]
    )

    const isRecoveringRef = useRef(false)

    // Recuperación de sesión desde Firebase si sessionStorage está vacío
    const recoverSessionFromFirebase = useCallback(async () => {
        // Sin esperar la persistencia, `currentUser` se leería contra IndexedDB (compartida por origen)
        await authReady

        // eslint-disable-next-line no-console
        console.info('[Session Recovery] Iniciando...', {
            hasToken: !!token,
            hasFbUser: !!fbUser,
            firebaseCurrentUser: !!firebase.auth().currentUser,
            hasLoggedOutIntentionally,
        })

        // Si fue logout intencional, no recuperar
        if (hasLoggedOutIntentionally) {
            // eslint-disable-next-line no-console
            console.info('[Session Recovery] Logout intencional, no recuperando')
            setIsRecoveringSession(false)
            return
        }

        if (token) {
            // eslint-disable-next-line no-console
            console.info('[Session Recovery] Ya hay token, saltando recuperación')
            setIsRecoveringSession(false)
            return
        }

        // Esperar a que Firebase se inicialice (onAuthStateChanged puede no haber disparado aún)
        const currentFbUser = firebase.auth().currentUser
        if (!currentFbUser) {
            // eslint-disable-next-line no-console
            console.info('[Session Recovery] No hay usuario en Firebase, esperando onAuthStateChanged...')
            // No setear isRecoveringSession a false aquí - dejar que onAuthStateChanged maneje
            return
        }

        if (isRecoveringRef.current) return
        isRecoveringRef.current = true

        try {
            // eslint-disable-next-line no-console
            console.info('[Session Recovery] Recuperando token desde Firebase...')
            const idToken = await currentFbUser.getIdToken(true)
            await verifyIdToken(idToken)
            // eslint-disable-next-line no-console
            console.info('[Session Recovery] Sesión recuperada exitosamente')
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('[Session Recovery] Error:', error)
        } finally {
            setIsRecoveringSession(false)
            isRecoveringRef.current = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, fbUser, hasLoggedOutIntentionally, verifyIdToken, setIsRecoveringSession])

    // Fallback timeout to avoid hanging the router indefinitely while recovering session
    useEffect(() => {
        if (!isRecoveringSession) return
        const t = setTimeout(() => {
            // eslint-disable-next-line no-console
            console.warn('[Session Recovery] Timeout reached, stopping recovery state')
            setIsRecoveringSession(false)
        }, SESSION_RECOVERY_TIMEOUT_MS)
        return () => clearTimeout(t)
    }, [isRecoveringSession])

    const refreshToken = (immediate: boolean) => {
        // No ejecutar durante recuperación inicial
        if (isRecoveringSession) return

        setNewAccessToken(token ?? null)
        if (fbUser && token) {
            const exp = jwtDecode<Token>(token).exp
            const diff = exp * 1000 - new Date().valueOf() - 10000
            if (diff > 0) {
                const time = DayJs().add(diff, 'milliseconds').fromNow()
                // eslint-disable-next-line no-console
                console.info(`The token will be refreshed ${time}`)
                const timerOut = setTimeout(
                    () => {
                        // eslint-disable-next-line no-console
                        console.info('Refreshing token...')
                        fbUser
                            .getIdToken(true)
                            .then(verifyIdToken)
                            .catch(function (error) {
                                // eslint-disable-next-line no-console
                                console.error(error)
                                logoutWithoutUser()
                            })
                    },
                    immediate ? 1000 : Math.floor(diff)
                )
                return () => clearTimeout(timerOut)
            } else {
                // eslint-disable-next-line no-console
                console.info('Expired token, refreshing it')
                // Esto genera un doble count en loginCount y genera que no se muestren los wizards @TO-DO
                fbUser
                    .getIdToken(true)
                    .then(verifyIdToken)
                    .catch(function () {
                        logoutWithoutUser()
                    })
            }
        } else if (fbUser === null && !isRecoveringSession) {
            setToken(undefined)
        }
    }

    useEffect(
        function AssignRole() {
            if (token) {
                const decoded = jwtDecode<Token>(token)
                decoded.role && setRole(decoded.role)
            }
        },
        [setRole, token]
    )

    useLayoutEffect(() => {
        refreshToken(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fbUser, setToken, token, verifyIdToken])

    useLayoutEffect(
        function AddAuthListener() {
            let unSuscribe: firebase.Unsubscribe | undefined
            let cancelled = false

            // Esperar a que la persistencia quede fijada por pestaña: suscribirse antes haría que el
            // primer estado se leyera contra la persistencia vieja (IndexedDB, compartida por origen).
            authReady.then(() => {
                if (cancelled) return
                unSuscribe = firebase.auth().onAuthStateChanged(handleAuthStateChanged)
            })

            return () => {
                cancelled = true
                unSuscribe?.()
            }
        },
        [handleAuthStateChanged]
    )

    // Recuperación de sesión al montar
    useEffect(() => {
        recoverSessionFromFirebase()
    }, [recoverSessionFromFirebase])

    // Suscripción a BroadcastChannel
    useEffect(() => {
        const unsubscribe = authBroadcast.subscribe((message) => {
            switch (message.type) {
                case 'TOKEN_REQUEST':
                    if (token) {
                        authBroadcast.broadcastToken(token)
                    }
                    break
                case 'TOKEN_UPDATE': {
                    // Responder a actualización de token (otro tab respondió a una request)
                    const newToken = message.token
                    if (!token && !hasLoggedOutIntentionally && newToken) {
                        setToken(newToken)
                    }
                    break
                }
                case 'LOGOUT':
                    // Incoming logout — avoid re-broadcasting when applying
                    // message is narrowed to LOGOUT here, so access identity/token safely
                    if (authBroadcast.shouldLogout(message.identity ?? message.token, token ?? null)) {
                        // ensure we don't attempt recovery concurrently
                        logoutWithoutUser({ broadcast: false })
                    }
                    break
            }
        })
        return unsubscribe
    }, [token, logoutWithoutUser, hasLoggedOutIntentionally, setToken])

    // Limpieza del BroadcastChannel al desmontar el contexto
    // Limpieza del BroadcastChannel al cerrar la pestaña/ventana del navegador
    useEffect(() => {
        if (typeof window === 'undefined') {
            return
        }

        const handleBeforeUnload = () => {
            try {
                authBroadcast.destroy()
            } catch (e) {
                // ignore
            }
        }

        window.addEventListener('beforeunload', handleBeforeUnload)

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }
    }, [])

    const saveUser: ContextInterface['setUser'] = async (_user) => {
        if (!user?._id) throw Error('User ID not found')
        const response = await Axios.patch<IPatchUserResponse>(Config.API + '/user/' + user._id, _user)
        setUser({ ...user, ..._user })
        return response.data
    }

    const logout: ContextInterface['logout'] = async () => {
        if (!user) throw Error('Cannot logout. The user is not logged.')
        const oldUser = { ...user }
        const oldFBUser = fbUser
        const oldToken = token
        const currentToken = token

        try {
            // Mark intentional logout BEFORE clearing token to avoid race where
            // other tabs see token removed but not the logout flag.
            try {
                storageHelper.setHasLoggedOut(true)
                setHasLoggedOutIntentionally(true)
            } catch (e) {
                // ignore
            }

            setUser(undefined)
            setFBUser(null)
            setToken(undefined)

            if (currentToken) {
                try {
                    authBroadcast.broadcastLogout(currentToken)
                } catch (e) {
                    // restore previous auth state to avoid partial logout
                    setUser(oldUser)
                    setFBUser(oldFBUser)
                    setToken(oldToken)
                    try {
                        storageHelper.setHasLoggedOut(false)
                        setHasLoggedOutIntentionally(false)
                    } catch (er) {
                        // ignore
                    }
                    throw e
                }
            }

            storageHelper.clearSession()

            // Cerrar la sesión de Firebase de esta pestaña. Sin esto el logout es solo local y al
            // recargar `recoverSessionFromFirebase` canjea el refresh token y revive la sesión.
            // La persistencia es por pestaña (`SESSION`), así que esto no alcanza a las demás.
            await signOutFirebaseSession()
        } catch (error) {
            setUser(oldUser)
            setFBUser(oldFBUser)
            setToken(oldToken)
            try {
                storageHelper.setHasLoggedOut(false)
                setHasLoggedOutIntentionally(false)
            } catch (er) {
                // ignore
            }
            throw error
        }
    }

    const login: ContextInterface['login'] = async (email, password, force) => {
        if ((user || user === undefined) && !force)
            throw Error('Cannot login. The user is already logged. Please, logout first')
        // Resetear flag de logout intencional al hacer login
        setHasLoggedOutIntentionally(false)
        const { user: newUser } = await firebase.auth().signInWithEmailAndPassword(email, password)
        const idToken = await newUser?.getIdToken()
        idToken && (await verifyIdToken(idToken))

        GA.event('login')
    }

    const verify: ContextInterface['verify'] = async (code) => {
        if (!user) throw Error('Cannot verify. The user is not logged.')
        const response = await Axios.post(`${Config.API}/user/verifyEmail`, { code })
        setUser({ ...user, emailVerified: true })
        firebase.auth().currentUser?.reload()
        return response
    }

    const changeRole: ContextInterface['setRole'] = async (role: ROLES) => {
        if (!user?.roles?.includes(role)) throw Error(`The user does not have the role ${role}`)

        const response = await Axios.post(Config.API + `/user/refreshToken`, { role })
        setToken(response.data.token)
    }

    const rolePromotionAttemptedRef = useRef<string | null>(null)

    useEffect(
        function PromoteToStaffRoleAfterDirectLogin() {
            // El login directo (email+password) no lleva el claim `_profileId`, así que `verifyToken`
            // cae al primer profile activo (para un coach multi-perfil suele ser uno de alumno). Si el
            // usuario en realidad tiene un rol de staff, reemitimos el token con ese rol para que el
            // control room lo trate como coach/profesor. El deep-link ya llega con rol de staff, así
            // que la condición corta antes y no entra acá.
            if (!token || !user?._id) return
            const targetRole = resolveRolePromotion(role, user.roles)
            if (!targetRole) return

            // Guard keyed en el token: evita reintentos en loop si el switch falla (el token no cambia),
            // pero permite volver a promover cuando el refresh de token (~24h) reemite un token student
            // nuevo — ese es un token distinto, así que no queda bloqueado.
            const attemptKey = token
            if (rolePromotionAttemptedRef.current === attemptKey) return
            rolePromotionAttemptedRef.current = attemptKey

            changeRole(targetRole).catch((error) => {
                // eslint-disable-next-line no-console
                console.warn('[Auth] No se pudo promover automáticamente al rol de staff:', error)
            })
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [token, user, role]
    )

    const fetchUser = useCallback(async () => {
        if (fbUser === undefined || !token) return
        if (fbUser) {
            try {
                const me = await Axios.get(Config.API + `/me`)
                setUser({
                    ...me.data.user,
                    _profileId: me?.data?.activeProfile?._id,
                    roles: me.data.roles,
                    //role: role,
                    emailVerified: fbUser.emailVerified,
                    notificationsCount: me?.data?.notifications?.count,
                    settings: me?.data?.settings,
                })
                setProfiles(me.data.profiles)

                // Updates intercom user data when fetching user
                if ('Intercom' in window) {
                    window.Intercom('update', {
                        email: me.data.user.email,
                        user_id: me.data.user._profileId,
                        name: `${me.data.user.name} ${me.data.user.lastname}`,
                        language_override: me.data.user.language,
                        created_at: me.data.user.createdAt,
                        avatar: me.data.user.urlImage
                            ? {
                                  type: 'avatar',
                                  image_url: me.data.user.urlImage,
                              }
                            : undefined,
                    })
                }

                // Sincronizar el idioma del usuario con i18n
                if (me.data.user.language && i18n.language) {
                    const userBaseLang = me.data.user.language.split('-')[0]
                    const i18nBaseLang = i18n.language.split('-')[0]
                    if (userBaseLang !== i18nBaseLang) {
                        i18n.changeLanguage(me.data.user.language)
                    }
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Comprueba tu conexión a internet.')
                refreshToken(true)
                // eslint-disable-next-line no-console
                console.error('REFRESH TOKEN 3')
                throw error
            }
        } else {
            setUser(null)
            setRole(undefined)
            setProfiles([])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fbUser, token, setRole])

    useEffect(() => {
        fetchUser()
    }, [fetchUser])

    useEffect(() => {
        if ('Intercom' in window && user)
            window.Intercom('update', {
                name: `${user.name} ${user.lastname}`,
                email: user.email,
                user_id: user._profileId,
                language_override: user.language,
                avatar: user.urlImage
                    ? {
                          type: 'avatar',
                          image_url: user.urlImage,
                      }
                    : undefined,
            })
    }, [user])

    const isCoach = role === 'professor'

    const value: ContextInterface = {
        user,
        setUser: saveUser,
        fetchUser,
        setUserButNotSaveIt: setUser,
        profiles,
        setProfiles,
        role,
        setRole: changeRole,
        setToken,
        token,
        fbUser,
        logged: !!jwtToken,
        logout,
        login,
        verify,
        loginSuperAdmin,
        loginWithIdToken,
        skipValidation,
        setSkipValidation,
        handleUploadPhoto,
        imageIsLoading,
        setImageIsLoading,
        isCoach,
        isRecoveringSession,
    }

    return value
}

export const useUser = () => useContext(UserContext)

export default UserContext

export interface Profile {
    _profileId: string
    role: ROLES
    hq: {
        _id: string
        name: string
    }
    course: {
        _id: string
        name: string
    }
}

export interface User {
    _id?: string
    _profileId?: string
    name?: string
    lastname?: string
    email: string
    phone?: string
    urlImage?: string
    language?: 'es' | 'en' | 'pt'
    dni?: string
    createdAt: string
    address?: {
        street?: string
        numeration?: string
        floor?: string
        apartment?: string
        country?: string
        city?: string
    }
    statusMessage?: string
    birthdate?: string
    //language?: string,
    roles?: ROLES[]
    role?: ROLES
    isCoach?: boolean
    emailVerified: boolean
    profiles?: Profile[]
    genre?: 'male' | 'female' | 'other'
    notificationsCount?: number | undefined
    settings: {
        mandatoryDataFill?: boolean
        avatarUploadSkipped?: boolean
        askForTermsAndConditions: boolean
    }
    termsAndConditions: boolean
}

export interface Token {
    role?: ROLES
    _profileId?: string
    exp: number
}

export interface IPatchUserResponse {
    _id: string
    email: string
    name: string
    lastname: string
    dni: string
    phone: string
    address: string //verify this
    statusMessage: string
    urlImage: string
    birthdate: string
    language: string
}
